import React, { useEffect, useState, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createUpdateLocation } from "../../../store/actions/locationsAction";
import Popup from "../../common/modal/Popup";
import Loader from "../../common/loader/Loader";
import ApiResponsePopup from "../../common/modal/ApiResponsePopup";

/**
 * ManageLocation - complete component with internal validation for email arrays
 *
 * Props:
 *  - selectLocation (object | null)
 *  - onHide (function)
 */
const ManageLocation = (props) => {
  const { selectLocation } = props;
  const dispatch = useDispatch();
  const inputRef = useRef();

  // redux slice (existing)
  const { error: apiError, isLoader } = useSelector((state) => state.locations);

  // local state
  const [isShow, setIsShow] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const [locationDetail, setLocationDetail] = useState({
    name: "",
    email: [],
    ccEmail: [],
  });

  // controlled inputs for typing before converting to tag
  const [emailInput, setEmailInput] = useState("");
  const [ccEmailInput, setCcEmailInput] = useState("");

  // validation state:
  // - name: string|null
  // - email: array of null|string (null means valid for that index)
  // - ccEmail: array of null|string
  const [errors, setErrors] = useState({
    name: null,
    email: null,
    ccEmail: null,
  });

  // Email regexp - adjustable
  const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  // Populate form when selectLocation changes
  useEffect(() => {
    if (!selectLocation) {
      setLocationDetail({ name: "", email: [], ccEmail: [] });
    } else {
      setLocationDetail({
        locationId: selectLocation._id || undefined,
        name: selectLocation.name || "",
        email: Array.isArray(selectLocation.email)
          ? selectLocation.email
          : selectLocation.email
          ? [selectLocation.email]
          : [],
        ccEmail: Array.isArray(selectLocation.ccEmail)
          ? selectLocation.ccEmail
          : selectLocation.ccEmail
          ? [selectLocation.ccEmail]
          : [],
      });
    }
    // clear inputs & errors
    setEmailInput("");
    setCcEmailInput("");
    setErrors({ name: null, email: null, ccEmail: null });
  }, [selectLocation]);

  // helpers
  const validateSingleEmail = (value) => {
    return emailRegex.test(String(value).trim());
  };

  const validateEmailArray = (arr, required = true) => {
    // returns array of null|errorText
    if (!arr || arr.length === 0) {
      if (required) return ["At least one email is required"];
      return [];
    }
    return arr.map((e) => (validateSingleEmail(e) ? null : "Invalid email"));
  };

  const hasEmailArrayError = (arrErr) => {
    if (!arrErr) return false;
    if (!Array.isArray(arrErr)) return !!arrErr; // fallback
    // consider error if any element is non-null OR array contains "At least one email is required"
    return arrErr.length === 0
      ? false
      : arrErr.some((x) => x !== null && x !== undefined);
  };

  // Full form validation - returns boolean
  const validateAll = () => {
    const nameErr = !locationDetail.name || !locationDetail.name.trim()
      ? "Please enter geography name"
      : null;

    const emailErrArr = validateEmailArray(locationDetail.email, true); // required
    const ccEmailErrArr = validateEmailArray(locationDetail.ccEmail, false); // optional

    const newErrors = {
      name: nameErr,
      email: emailErrArr,
      ccEmail: ccEmailErrArr,
    };

    setErrors(newErrors);

    // valid if nameErr null AND no email errors
    const emailHasErr = hasEmailArrayError(emailErrArr);
    const ccEmailHasErr = hasEmailArrayError(ccEmailErrArr);

    return !nameErr && !emailHasErr && !ccEmailHasErr;
  };

  // Add email helper (prevents duplicates, trims)
  const addEmail = (rawValue, kind = "email") => {
    const value = String(rawValue || "").trim();
    if (!value) return { added: false, reason: "empty" };
    if (!validateSingleEmail(value)) return { added: false, reason: "invalid" };

    const currentList = locationDetail[kind] || [];

    // prevent duplicates (case-insensitive)
    const exists = currentList.some(
      (e) => String(e).trim().toLowerCase() === value.toLowerCase()
    );
    if (exists) return { added: false, reason: "duplicate" };

    const updated = [...currentList, value];
    setLocationDetail((prev) => ({ ...prev, [kind]: updated }));
    // clear per-kind input
    if (kind === "email") setEmailInput("");
    else setCcEmailInput("");

    // reset corresponding errors
    setErrors((prev) => ({ ...prev, [kind]: validateEmailArray(updated, kind === "email") }));

    return { added: true };
  };

  // remove email from array
  const removeEmail = (index, kind = "email") => {
    const arr = locationDetail[kind] || [];
    const updated = arr.filter((_, i) => i !== index);
    setLocationDetail((prev) => ({ ...prev, [kind]: updated }));
    setErrors((prev) => ({ ...prev, [kind]: validateEmailArray(updated, kind === "email") }));
  };

  // submit handler
  const submitHandler = async (e) => {
    e.preventDefault();
    const ok = validateAll();
    if (!ok) {
      // focus first invalid field if desired
      return;
    }

    // FINAL JSON Payload
    const jsonPayload = {
      locationId: locationDetail.locationId || undefined,
      name: locationDetail.name,
      email: locationDetail.email,
      ccEmail: locationDetail.ccEmail,
    };

    // dispatch action
    try {
      const { payload } = await dispatch(createUpdateLocation(jsonPayload));
      if (payload && payload.success) {
        setSuccessMessage(payload.message || "Saved successfully");
        // close popup or show success
        setIsShow(true);
        // optionally reset state or call onHide after a short success
        // form reset:
        setLocationDetail({ name: "", email: [], ccEmail: [] });
        setEmailInput("");
        setCcEmailInput("");
        // here we'll reset but keep popup open until user clicks OK on ApiResponsePopup
      } else {
        // show api error UI
        setIsShow(true);
      }
    } catch (err) {
      // dispatch may throw; show popup
      setIsShow(true);
    }
  };

  // Small helper to render email chips
  const renderChips = (arr, kind = "email") =>
    (arr || []).map((item, idx) => (
      <span
        key={`${kind}-${idx}`}
        style={{
          background: "#e1e1e1",
          borderRadius: "12px",
          padding: "4px 10px",
          display: "flex",
          alignItems: "center",
          marginRight: "5px",
          marginBottom: "5px",
        }}
      >
        <span style={{ fontSize: 13 }}>{item}</span>
        <span
          style={{
            marginLeft: "8px",
            cursor: "pointer",
            color: "#dc3545",
            fontWeight: "700",
          }}
          onClick={() => removeEmail(idx, kind)}
          aria-label={`Remove ${kind} ${item}`}
        >
          &times;
        </span>
      </span>
    ));

  // expose hide handler (closes popup)
  const hideHandler = () => {
    setErrors({ name: null, email: null, ccEmail: null });
    props.onHide && props.onHide();
    setLocationDetail({ name: "", email: [], ccEmail: [] });
    setEmailInput("");
    setCcEmailInput("");
  };

  const hideHandlerRes = () => {
    setIsShow(false);
  };

  const { onHide, ...props1 } = props;

  return (
    <>
      <Popup
        {...props1}
        onHide={hideHandler}
        size="md"
        footer={
          <>
            <Button variant="outline-secondary model-btn me-4" onClick={hideHandler}>
              Cancel
            </Button>
            <Button className="btn-primary model-btn" onClick={submitHandler}>
              {selectLocation ? "Update" : "Add"}
            </Button>
          </>
        }
      >
        {isLoader && <Loader />}

        {/* Geography Name */}
        <Form.Group className="mb-4 manageForm px-4" controlId="formBasicName">
          <Form.Label>
            Geography Name <span className="required">*</span>
          </Form.Label>
          <Form.Control
            className="py-2"
            type="text"
            name="name"
            placeholder="Enter Geography Name"
            value={locationDetail.name || ""}
            disabled={selectLocation && selectLocation._id ? true : false}
            onChange={(e) => {
              setLocationDetail({ ...locationDetail, name: e.target.value });
              // reset error on change
              setErrors((prev) => ({ ...prev, name: null }));
            }}
            isInvalid={!!errors.name}
          />
          <Form.Control.Feedback type="invalid">
            <p className="textDanger">{errors.name}</p>
          </Form.Control.Feedback>
        </Form.Group>

        {/* Email Tag Input */}
        <Form.Group className="mb-4 manageForm px-4" controlId="formBasicEmail">
          <Form.Label>
            To Email <span className="required">*</span>
          </Form.Label>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
              minHeight: "40px",
              border:
                errors &&
                errors.email &&
                Array.isArray(errors.email) &&
                errors.email.some((e) => e !== null)
                  ? "1px solid #dc3545"
                  : "1px solid #ced4da",
              borderRadius: "4px",
              padding: "6px",
            }}
            onClick={() => {
              // focus the hidden input area for convenience
              inputRef.current && inputRef.current.focus();
            }}
          >
            {renderChips(locationDetail.email, "email")}

            <input
              ref={inputRef}
              type="text"
              value={emailInput}
              style={{ border: "none", outline: "none", flex: 1, minWidth: "140px" }}
              placeholder="Enter email and press Enter"
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === ",") && emailInput.trim()) {
                  e.preventDefault();
                  const res = addEmail(emailInput, "email");
                  if (!res.added) {
                    // set error for email array if invalid or duplicate
                    const msg =
                      res.reason === "invalid"
                        ? ["Invalid email"]
                        : res.reason === "duplicate"
                        ? ["Duplicate email"]
                        : ["At least one email is required"];
                    setErrors((prev) => ({ ...prev, email: msg }));
                  }
                } else if (e.key === "Backspace" && !emailInput && locationDetail.email.length) {
                  // remove last tag on backspace when input empty
                  removeEmail(locationDetail.email.length - 1, "email");
                }
              }}
              onBlur={() => {
                if (emailInput.trim()) {
                  const res = addEmail(emailInput, "email");
                  if (!res.added) {
                    const msg =
                      res.reason === "invalid"
                        ? ["Invalid email"]
                        : res.reason === "duplicate"
                        ? ["Duplicate email"]
                        : ["At least one email is required"];
                    setErrors((prev) => ({ ...prev, email: msg }));
                  }
                }
              }}
            />
          </div>
 
          {/* Email-specific error rendering */}
          {errors &&
            errors.email &&
            Array.isArray(errors.email) &&
            errors.email.some((e) => e !== null) && (
              <div className="textDanger" style={{ color: "#dc3545", fontSize: "13px", marginTop: 6 }}>
                {errors.email.includes("At least one email is required")
                  ? "Please enter at least one email."
                  : "One or more entered emails are invalid."}
              </div>
            )}
        </Form.Group>
            
        {/* CC Email Tag Input (optional) */}
        <Form.Group className="mb-4 manageForm px-4" controlId="formBasicCcEmails">
          <Form.Label>CC Email (optional)</Form.Label>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
              minHeight: "40px",
              border:
                errors &&
                errors.ccEmail &&
                Array.isArray(errors.ccEmail) &&
                errors.ccEmail.some((e) => e !== null)
                  ? "1px solid #dc3545"
                  : "1px solid #ced4da",
              borderRadius: "4px",
              padding: "6px",
            }}
          >
            {renderChips(locationDetail.ccEmail, "ccEmail")}

            <input
              type="text"
              value={ccEmailInput}
              style={{ border: "none", outline: "none", flex: 1, minWidth: "140px" }}
              placeholder="Enter CC email and press Enter"
              onChange={(e) => setCcEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === ",") && ccEmailInput.trim()) {
                  e.preventDefault();
                  const res = addEmail(ccEmailInput, "ccEmail");
                  if (!res.added) {
                    const msg =
                      res.reason === "invalid"
                        ? ["Invalid email"]
                        : res.reason === "duplicate"
                        ? ["Duplicate email"]
                        : [];
                    setErrors((prev) => ({ ...prev, ccEmail: msg }));
                  }
                } else if (e.key === "Backspace" && !ccEmailInput && locationDetail.ccEmail.length) {
                  removeEmail(locationDetail.ccEmail.length - 1, "ccEmail");
                }
              }}
              onBlur={() => {
                if (ccEmailInput.trim()) {
                  const res = addEmail(ccEmailInput, "ccEmail");
                  if (!res.added) {
                    const msg =
                      res.reason === "invalid"
                        ? ["Invalid email"]
                        : res.reason === "duplicate"
                        ? ["Duplicate email"]
                        : [];
                    setErrors((prev) => ({ ...prev, ccEmail: msg }));
                  }
                }
              }}
            />
          </div>
               
          {errors &&
            errors.ccEmail &&
            Array.isArray(errors.ccEmail) &&
            errors.ccEmail.some((e) => e !== null) && (
              <div className="textDanger" style={{ color: "#dc3545", fontSize: "13px", marginTop: 6 }}>
                One or more CC emails are invalid.
              </div>
            )}
            <div
                        className="requiredNote"
                        style={{ marginTop: 10, position: "absolute" }}
                      >
                        Hint: Type an email and press 'Enter' to add multiple emails.
                      </div>
        </Form.Group>

        <ApiResponsePopup
          {...{
            hideHandlerRes,
            isRecordUpdated: isShow,
            title: "Manage Geography",
            successMsg: successMessage,
            errorRes: apiError?.message,
          }}
        />
      </Popup>
    </>
  );
};

export default ManageLocation;
